import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin } from "lucide-react";
import { turkeyProvinces } from "@/data/turkeyPaths";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// City coordinates on SVG map
const cityCoords = {
  "Adana":{cx:470,cy:310},"Adıyaman":{cx:600,cy:270},"Afyonkarahisar":{cx:290,cy:220},"Ağrı":{cx:830,cy:155},"Amasya":{cx:500,cy:145},"Ankara":{cx:340,cy:180},"Antalya":{cx:300,cy:320},"Artvin":{cx:690,cy:95},"Aydın":{cx:190,cy:270},"Balıkesir":{cx:175,cy:155},"Bilecik":{cx:260,cy:155},"Bingöl":{cx:720,cy:195},"Bitlis":{cx:790,cy:205},"Bolu":{cx:310,cy:135},"Burdur":{cx:280,cy:280},"Bursa":{cx:220,cy:145},"Çanakkale":{cx:140,cy:145},"Çankırı":{cx:385,cy:140},"Çorum":{cx:440,cy:140},"Denizli":{cx:230,cy:265},"Diyarbakır":{cx:680,cy:240},"Edirne":{cx:90,cy:95},"Elazığ":{cx:640,cy:215},"Erzincan":{cx:660,cy:170},"Erzurum":{cx:740,cy:150},"Eskişehir":{cx:280,cy:175},"Gaziantep":{cx:560,cy:300},"Giresun":{cx:590,cy:120},"Gümüşhane":{cx:640,cy:130},"Hakkari":{cx:880,cy:240},"Hatay":{cx:520,cy:340},"Isparta":{cx:275,cy:260},"Mersin":{cx:420,cy:330},"İstanbul":{cx:195,cy:115},"İzmir":{cx:160,cy:225},"Kars":{cx:810,cy:120},"Kastamonu":{cx:420,cy:105},"Kayseri":{cx:480,cy:220},"Kırklareli":{cx:115,cy:90},"Kırşehir":{cx:430,cy:195},"Kocaeli":{cx:240,cy:115},"Konya":{cx:370,cy:260},"Kütahya":{cx:250,cy:195},"Malatya":{cx:590,cy:230},"Manisa":{cx:185,cy:215},"Kahramanmaraş":{cx:545,cy:270},"Mardin":{cx:750,cy:260},"Muğla":{cx:210,cy:300},"Muş":{cx:770,cy:190},"Nevşehir":{cx:445,cy:225},"Niğde":{cx:440,cy:260},"Ordu":{cx:560,cy:120},"Rize":{cx:660,cy:105},"Sakarya":{cx:260,cy:125},"Samsun":{cx:520,cy:115},"Siirt":{cx:780,cy:230},"Sinop":{cx:475,cy:95},"Sivas":{cx:560,cy:180},"Tekirdağ":{cx:120,cy:105},"Tokat":{cx:530,cy:155},"Trabzon":{cx:630,cy:110},"Tunceli":{cx:680,cy:200},"Şanlıurfa":{cx:630,cy:280},"Uşak":{cx:240,cy:230},"Van":{cx:840,cy:200},"Yozgat":{cx:460,cy:185},"Zonguldak":{cx:300,cy:105},"Aksaray":{cx:420,cy:240},"Bayburt":{cx:680,cy:130},"Karaman":{cx:380,cy:285},"Kırıkkale":{cx:395,cy:175},"Batman":{cx:740,cy:240},"Şırnak":{cx:820,cy:255},"Bartın":{cx:325,cy:95},"Ardahan":{cx:780,cy:100},"Iğdır":{cx:860,cy:140},"Yalova":{cx:220,cy:130},"Karabük":{cx:345,cy:115},"Kilis":{cx:555,cy:310},"Osmaniye":{cx:510,cy:305},"Düzce":{cx:295,cy:125},"Midyat":{cx:715,cy:275}
};

// Fallback hardcoded pins when no API pins exist
const defaultPins = [
  { id: "default-1", city: "Kocaeli", label: "Sanayi Tesisleri", project_name: "Gebze Lojistik Merkezi", project_detail: "50.000 m² endüstriyel tesis" },
  { id: "default-2", city: "Batman", label: "Konut Projesi", project_name: "Batman Konut Sitesi", project_detail: "Modern konut projesi" },
  { id: "default-3", city: "Mardin", label: "Kamu Projesi", project_name: "Mardin Yeni Şehir", project_detail: "Şehir konutları" },
  { id: "default-4", city: "Şırnak", label: "Altyapı", project_name: "Şırnak Toplu Konut", project_detail: "Toplu konut projesi" },
  { id: "default-5", city: "Midyat", label: "Kültür Merkezi", project_name: "Midyat Kültür Merkezi", project_detail: "Kamu yapısı" },
  { id: "default-6", city: "Hakkari", label: "Sağlık", project_name: "Hakkari Devlet Hastanesi", project_detail: "Sağlık tesisi" },
];

const pinColors = ["#FF5A00", "#22C55E", "#F59E0B", "#3B82F6", "#8B5CF6", "#EC4899"];

export default function TurkeyMap() {
  const [pins, setPins] = useState([]);
  const [selected, setSelected] = useState(null);
  const [hoveredProvince, setHoveredProvince] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/map-pins`).then((r) => {
      if (r.data.length > 0) {
        setPins(r.data.map((p, i) => ({
          ...p,
          ...(cityCoords[p.city] || { cx: 500, cy: 200 }),
          color: pinColors[i % pinColors.length],
        })));
      } else {
        setPins(defaultPins.map((p, i) => ({
          ...p,
          ...(cityCoords[p.city] || { cx: 500, cy: 200 }),
          color: pinColors[i % pinColors.length],
        })));
      }
    }).catch(() => {
      setPins(defaultPins.map((p, i) => ({ ...p, ...(cityCoords[p.city] || { cx: 500, cy: 200 }), color: pinColors[i % pinColors.length] })));
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setSelected(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <section data-testid="turkey-map-section" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
          <p className="overline justify-center mb-5">Proje Konumları</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-[#0C0C0C]" style={{ fontFamily: "Outfit" }}>
            Türkiye genelinde <span className="text-[#FF5A00]">projelerimiz</span>
          </h2>
          <p className="text-gray-500 mt-4 max-w-md mx-auto text-sm">Harita üzerindeki pinlere tıklayarak proje detaylarını görüntüleyebilirsiniz</p>
        </motion.div>

        <motion.div ref={containerRef} initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-4 sm:p-8 overflow-hidden">
          <svg viewBox="0 0 1000 422" className="w-full h-auto" data-testid="turkey-map-svg">
            {turkeyProvinces.map((province) => (
              <path key={province.id} d={province.d} fill={hoveredProvince === province.id ? "#e5e7eb" : "#f0f0f0"} stroke="#d1d5db" strokeWidth="0.8" className="transition-colors duration-200 cursor-pointer"
                onMouseEnter={() => setHoveredProvince(province.id)} onMouseLeave={() => setHoveredProvince(null)}>
                <title>{province.name}</title>
              </path>
            ))}
            {pins.map((pin) => (
              <g key={pin.id} className="cursor-pointer" onClick={() => setSelected(selected?.id === pin.id ? null : pin)} data-testid={`map-pin-${pin.city}`}>
                <circle cx={pin.cx} cy={pin.cy} r="10" fill="none" stroke={pin.color} strokeWidth="1.5" opacity="0.3">
                  <animate attributeName="r" from="6" to="18" dur="2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" from="0.5" to="0" dur="2s" repeatCount="indefinite" />
                </circle>
                <g transform={`translate(${pin.cx}, ${pin.cy - 18})`}>
                  <ellipse cx="0" cy="22" rx="5" ry="2" fill="rgba(0,0,0,0.15)" />
                  <path d="M0,-14 C-8,-14 -14,-8 -14,0 C-14,5 -8,12 0,20 C8,12 14,5 14,0 C14,-8 8,-14 0,-14 Z" fill={pin.color} stroke="white" strokeWidth="1.5" />
                  <circle cx="0" cy="-1" r="7" fill="white" opacity="0.9" />
                  <text x="0" y="3" textAnchor="middle" fontSize="8" fill={pin.color} fontWeight="bold" fontFamily="Outfit">
                    <MapPin size={8} />
                  </text>
                </g>
                <text x={pin.cx} y={pin.cy + 12} textAnchor="middle" fill="#374151" fontSize="7.5" fontWeight="600" fontFamily="Outfit, sans-serif">
                  {pin.city}
                </text>
              </g>
            ))}
          </svg>

          {/* Popup */}
          <AnimatePresence>
            {selected && (
              <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} transition={{ duration: 0.25 }}
                className="absolute top-4 right-4 sm:top-6 sm:right-6 w-[280px] sm:w-[320px] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-20" data-testid="map-popup">
                <div className="flex items-center justify-between p-4 border-b border-gray-100" style={{ background: `linear-gradient(135deg, ${selected.color}10, ${selected.color}05)` }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: selected.color }}>
                      <MapPin size={18} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0C0C0C] text-base" style={{ fontFamily: "Outfit" }}>{selected.city}</h3>
                      <p className="text-xs text-gray-400">{selected.label}</p>
                    </div>
                  </div>
                  <button onClick={() => setSelected(null)} className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors" data-testid="map-popup-close">
                    <X size={14} />
                  </button>
                </div>
                <div className="p-4 space-y-3">
                  {selected.project_name && (
                    <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                      <p className="text-sm font-semibold text-[#0C0C0C]" style={{ fontFamily: "Outfit" }}>{selected.project_name}</p>
                      {selected.project_detail && <p className="text-xs text-gray-500 mt-1">{selected.project_detail}</p>}
                    </div>
                  )}
                  {selected.description && <p className="text-xs text-gray-400">{selected.description}</p>}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
              <div className="text-xs font-bold text-[#0C0C0C]" style={{ fontFamily: "Outfit" }}>
                Toplam: {pins.length} Konum
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
